/**
 * BlockedPage — akkaunt bloklangan sahifasi (spec bo'lim 4)
 *
 * Ochiladi: obuna tugagan, to'lov kelmagan yoki admin bloklagan.
 * Foydalanuvchi bu sahifadan "Login sahifasiga qaytish" orqali chiqishi kerak.
 */

import { AlertTriangle, ArrowLeft, Phone } from 'lucide-react';
import { useNavigate } from 'react-router';

import { useCurrentUser, useLogout } from '@/features/auth/auth.hooks';
import { cn } from '@/shared/lib/cn';

const DEFAULT_HELP_PHONE = '+998 90 000 00 00';

export function BlockedPage(): React.ReactElement {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const logout = useLogout();

  // Subscription info (spec bo'lim 4 — obuna ma'lumotlari bloki)
  const subscriptionType = user?.subscriptionType ?? 'TRIAL';
  const subscriptionEnd = user?.subscriptionEnd ?? null;
  const helpPhone = DEFAULT_HELP_PHONE;

  const handleBackToLogin = (): void => {
    logout();
    void navigate('/login', { replace: true });
  };

  const formattedUntil =
    subscriptionEnd !== null ? new Date(subscriptionEnd).toISOString().slice(0, 10) : null;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Main card */}
        <div className="space-y-5 rounded-2xl border border-destructive/40 bg-card p-6 shadow-xl">
          {/* Alert icon + title */}
          <div className="flex flex-col items-center text-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-destructive/15">
              <AlertTriangle className="size-8 text-destructive" aria-hidden />
            </div>
            <h1 className="mt-4 text-2xl font-bold">Akkaunt bloklangan</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Obuna muddati tugagan yoki to&apos;lov amalga oshirilmagan. Iltimos, quyidagi raqamga
              qo&apos;ng&apos;iroq qiling va to&apos;lovni amalga oshiring.
            </p>
          </div>

          {/* Subscription info block */}
          <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-4 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Plan:</span>
              <span className="font-medium">
                {subscriptionType === 'LIFETIME' && 'Cheksiz'}
                {subscriptionType === 'YEARLY' && 'Yillik'}
                {subscriptionType === 'MONTHLY' && 'Oylik'}
                {subscriptionType === 'TRIAL' && 'Sinov (Trial)'}
              </span>
            </div>
            {formattedUntil !== null && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Tugash sanasi:</span>
                <span className="font-medium">{formattedUntil}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">Holat:</span>
              <span className="font-medium text-destructive">⚠️ Muddati o&apos;tgan</span>
            </div>
          </div>

          {/* Phone contact */}
          <a
            href={`tel:${helpPhone.replace(/\s/g, '')}`}
            className="flex items-center gap-3 rounded-lg border border-border bg-muted/50 p-4 transition-colors hover:bg-muted"
          >
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <Phone className="size-5 text-primary" aria-hidden />
            </div>
            <div className="flex-1">
              <p className="text-xs text-muted-foreground">Yordam raqami</p>
              <p className="text-lg font-bold tracking-wider">{helpPhone}</p>
            </div>
          </a>

          {/* Info text */}
          <p className="rounded-lg bg-info/10 p-3 text-xs leading-relaxed text-info">
            To&apos;lovni amalga oshirgandan so&apos;ng, akkauntingiz avtomatik ravishda
            faollashtiriladi va siz qaytadan kirishingiz mumkin bo&apos;ladi.
          </p>

          {/* Back to login */}
          <button
            type="button"
            onClick={handleBackToLogin}
            className={cn(
              'flex h-11 w-full items-center justify-center gap-2 rounded-lg',
              'border border-border bg-transparent text-sm font-medium',
              'transition-colors hover:bg-muted',
            )}
          >
            <ArrowLeft className="size-4" aria-hidden />
            Kirish sahifasiga qaytish
          </button>
        </div>
      </div>
    </div>
  );
}
